import { type DataTableColumn, DataTable } from '@/components/DataTable';
import { PageHeader, PageShell } from '@/components/PageShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useServerSort } from '@/lib/tableSort';
import { useLoadMore } from '@rock/db-admin';
import { companiesState } from '@/state';
import type { Company } from '@rock/types';
import type { FC } from 'react';

export const CompaniesListPage: FC = () => {
    const { data: companies, isLoading, hasError, hasMore, loadMore, sort, setSort } = useLoadMore(companiesState);

    const { sort: tableSort, setSort: setTableSort } = useServerSort(COMPANY_COLUMNS, { sort, setSort });

    return (
        <PageShell>
            <PageHeader
                title="Companies"
                description="Catalog of service providers referenced by home Contacts. Records are created automatically when users add a contact from the app."
            />
            {companies.length === 0 ? (
                <Card>
                    <CardHeader>
                        <CardTitle>
                            {hasError
                                ? 'Couldn’t load companies'
                                : isLoading
                                  ? 'Loading companies…'
                                  : 'No companies yet'}
                        </CardTitle>
                        <CardDescription>
                            {hasError
                                ? 'You may not have permission to view this page.'
                                : isLoading
                                  ? 'Fetching the company catalog.'
                                  : 'As users add contacts from the app, companies will appear here.'}
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle>Companies</CardTitle>
                        <CardDescription>Tap any row to inspect or override brand details.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <DataTable
                            columns={COMPANY_COLUMNS}
                            rows={companies}
                            rowKey={(company) => company.id}
                            rowHref={(company) => `/directory/companies/${encodeURIComponent(company.id)}`}
                            sort={tableSort}
                            onSortChange={setTableSort}
                        />
                        {hasMore && (
                            <div className="mt-4 flex justify-center">
                                <Button variant="outline" onClick={() => loadMore()}>
                                    Load More
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </PageShell>
    );
};

/**
 * Logo and Brand are derived from whichever of several fields happens to be set, so neither has a
 * field to order by. Phone would sort by area code — true, and useless.
 */
const COMPANY_COLUMNS: DataTableColumn<Company>[] = [
    {
        key: 'logo',
        header: 'Logo',
        className: 'min-w-[80px]',
        cell: (company) => {
            const logoUrl = company.logoOverrideUrl ?? company.brand?.logoUrl ?? company.brand?.iconUrl;
            return logoUrl ? (
                <img
                    src={logoUrl}
                    alt={`${company.name} logo`}
                    className="h-8 w-8 rounded border bg-white object-contain"
                />
            ) : (
                <span className="text-xs text-muted-foreground">—</span>
            );
        },
    },
    {
        key: 'name',
        header: 'Name',
        className: 'min-w-[240px]',
        cellClassName: 'font-medium',
        sortField: 'name',
        cell: (company) => company.name,
    },
    {
        key: 'domain',
        header: 'Domain',
        className: 'min-w-[180px]',
        sortField: 'domain',
        sortNote: 'Sorted by Domain — companies without a domain are hidden.',
        cell: (company) => company.domain ?? '—',
    },
    {
        key: 'phone',
        header: 'Phone',
        className: 'min-w-[160px]',
        cell: (company) => company.phone ?? '—',
    },
    {
        key: 'brand',
        header: 'Brand',
        className: 'min-w-[120px]',
        cell: (company) =>
            company.logoOverrideUrl
                ? 'Override'
                : company.brand?.notFound
                  ? 'Not found'
                  : company.brand?.logoUrl
                    ? 'Fetched'
                    : '—',
    },
];
